import { ModalFooter } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Input } from "@hostingbv/lib/src/components/form/Input";
import { ObjectAutocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useRefresh, useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SshKey } from "@hostingbv/lib/src/api/partner/models/SshKey";
import { faWarning } from "@fortawesome/free-solid-svg-icons";


export const AddDeploymentKeyModal: FC<{ filterKeys: (key: SshKey) => boolean, onKeySelected: (depKey: SshKey) => void }> = (props) => {
  const { partnerClient } = useApiClient(config)

  const [name, setName] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [keyType, setKeyType] = useState<string>("Use existing")
  const [error, setError] = useState('')
  const [selectedKey, setSelectedKey] = useState<SshKey | null>(null)

  const { resource, reload } = useFetchedResource(() => partnerClient.getAllSshKeys())
  useRefreshEffect(() => {
    reload().then(response => {
      if (response?.success) {
        if (response.data.filter(props.filterKeys).length === 0) {
          setKeyType("Create new")
        }
      }
    })
  })
  const deploymentKeys = useMemo(() => resource?.success ? resource.data.filter(props.filterKeys) : [], [props.filterKeys, resource])
  useEffect(() => {
    if (deploymentKeys.length > 0 && selectedKey === null) {
      setSelectedKey(deploymentKeys[0])
    }
  }, [deploymentKeys, selectedKey])

  const refresh = useRefresh()

  const save = useCallback(async () => {
    if (keyType === "Use existing") {
      if (selectedKey !== null) {
        props.onKeySelected(selectedKey)
      }
      return
    }
    await partnerClient.createSshKey({ display_name: name, public_key: publicKey }).then(result => {
      if (!result.success) {
        setError(result.error)
        throw new Error(result.error)
      } else {
        refresh()
        console.log('running selected callback with key', result.data)
        props.onKeySelected(result.data)
      }
    })
  }, [name, publicKey, keyType, props.onKeySelected, selectedKey])

  const isShortRsaKey = useMemo(() => {
    const match = /^ssh-rsa\s[A-Za-z0-9+/=]+/.exec(publicKey)
    if (match === null) {
      return false
    }
    return match[0].length < 264
  }, [publicKey])
  const isStupid = useMemo(() => {
    return publicKey.includes('PRIVATE KEY-----')
  }, [publicKey])


  return <>
    <form onSubmit={() => save()} className={"space-y-4 flex-1"}>
      <TabLayout tabNames={["Use existing", "Create new"]} activeTab={keyType} setActiveTab={setKeyType} tabContents={[
        <>
          <ObjectAutocomplete<SshKey> label={'Available keys'} options={deploymentKeys} value={selectedKey} onChange={setSelectedKey} displayItem={key => key.displayName} idProperty={'id'} />
        </>,
        <>
          {isShortRsaKey && <div className={"flex items-center space-x-3  px-3 py-2 bg-amber-50 rounded-sm"}>
            <FontAwesomeIcon icon={faWarning} className={"text-amber-600"} />
            <span className={"font-medium text-slate-800 text-sm"}>
              You are using a short RSA key (&lt;3072 bit). <br />
              This is considered unsafe. Consider using a 4096 bit RSA key or an Ed25519 key.
            </span>
          </div>}
          {isStupid && <div className={"flex items-center space-x-3  px-3 py-2 bg-amber-50 rounded-sm"}>
            <span>🤦</span>
            <span className={"font-medium text-slate-800 text-sm"}>
              <strong>OH NO! You are using a private key.</strong><br />
              Never, ever, ever, share your private key with anyone including us. Use your public key instead.
            </span>
          </div>}
          <Input label={"Public Key"} type={"textarea"} placeholder={'ssh-ed25519 ...'} value={publicKey} onChange={(v) => setPublicKey(v)} />
          <div className={"flex items-center max-w-md space-x-4"}>
            <Input label={"Name"} type={"text"} value={name} onChange={(v) => setName(v)} />
          </div>
        </>
      ]} />
      {error.length > 0 && <div className={"font-medium text-red-600"}>
        {error}
      </div>}
    </form>
    <ModalFooter text={"Save"} onSubmit={save} />
  </>
}
